import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "../ui/sheet";
import { ShoppingBag, Search, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "../../context/CartContext";

const SAR = (n) => (n || 0).toLocaleString("en-US");

export const CartDrawer = ({ open, onOpenChange }) => {
    const { t } = useTranslation();
    const { items, remove, total } = useCart();
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full max-w-md bg-limestone-100 border-s-2 border-ink-200"
                data-testid="cart-drawer"
            >
                <SheetHeader>
                    <SheetTitle className="font-display text-[24px] font-bold tracking-tight text-ink-800">
                        {t("cart.title")} <span className="text-madder-500 num-ltr">({items.length})</span>
                    </SheetTitle>
                </SheetHeader>

                {items.length === 0 ? (
                    <div className="mt-8 flex flex-col items-center justify-center rounded-card border-2 border-dashed border-ink-300 bg-white px-6 py-14 text-center">
                        <ShoppingBag className="mb-4 h-10 w-10 text-ink-400" />
                        <p className="font-display text-[20px] font-bold text-ink-800">
                            {t("cart.empty")}
                        </p>
                        <p className="mt-2 text-[14px] font-medium text-ink-600">
                            {t("cart.emptyHint")}
                        </p>
                        <Link
                            to="/home-a#hero-search"
                            onClick={() => onOpenChange(false)}
                            data-testid="cart-search-cta"
                            className="mt-6 inline-flex h-[48px] items-center gap-2 rounded-btn bg-ink-800 px-5 text-[14px] font-bold text-white hover:bg-madder-500"
                        >
                            <Search className="h-4 w-4" />
                            {t("cart.search")}
                        </Link>
                    </div>
                ) : (
                    <div className="mt-6 flex h-[calc(100vh-180px)] flex-col">
                        <div className="flex-1 overflow-y-auto space-y-3" data-testid="cart-drawer-items">
                            {items.map((it) => (
                                <div key={it.domain} className="flex items-center justify-between rounded-card border-2 border-ink-200 bg-white px-4 py-3" data-testid={`drawer-item-${it.domain}`}>
                                    <div>
                                        <p className="font-mono text-[15px] font-bold text-ink-800 num-ltr">{it.domain}</p>
                                        <p className="font-mono text-[12px] font-semibold text-madder-500 num-ltr">{it.price_sar} SAR / yr</p>
                                    </div>
                                    <button
                                        onClick={() => remove(it.domain)}
                                        aria-label="Remove"
                                        data-testid={`drawer-remove-${it.domain}`}
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-btn border-2 border-ink-200 text-ink-600 hover:border-madder-500 hover:text-madder-500"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 rounded-card border-2 border-ink-800 bg-white p-4">
                            <div className="flex items-baseline justify-between">
                                <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-ink-600">Subtotal</span>
                                <span className="font-display text-[24px] font-extrabold tabular-nums num-ltr text-madder-500">{SAR(total)} SAR</span>
                            </div>
                            <Link
                                to="/cart"
                                onClick={() => onOpenChange(false)}
                                data-testid="drawer-checkout-btn"
                                className="mt-4 inline-flex h-[56px] w-full items-center justify-center gap-2 rounded-btn bg-madder-500 px-5 text-[16px] font-bold text-white hover:bg-madder-600"
                            >
                                Checkout
                                <ArrowRight className="h-4 w-4 rtl-mirror" />
                            </Link>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};
